/* eslint-disable no-unused-vars */
import FirebaseInstance from '@/services/FirebaseInstance.js'
import Logger from '@/services/Logger.js'
const logger = new Logger('Firebase Utils', true)

class FirebaseUtils {
  static error(error: any): void {
    if (error) {
      logger.error('Error in firebase operation', error)
    }
  }

  static createSyncedObject(
    $scope: any,
    path: string
  ): {
    on: (callback: (val: any) => void, noNullValues?: boolean) => void
    once: (callback: (val: any) => void, noNullValues?: boolean) => void
    off: () => void
    set: (val: any, callback?: (error?: any) => void) => void
    remove: (callback?: (error?: any) => void) => void
    increment: (byThisMany?: number, childName?: string) => void
    decrement: (byThisMany?: number, childName?: string) => void
    push: (val: any, callback?: () => void) => void
    setChild: (childName: string, val: any, callback?: () => void) => void
    removeChild: (childName: string, val: any, callback?: () => void) => void
    update: (val: any, callback?: () => void) => void
    updateChild: (childName: string, val: any, callback?: () => void) => void
    setOnDisconnectHandler: (
      action: 'update' | 'remove',
      updates: any,
      callback?: (error?: any) => void
    ) => void
    cancelOnDisconnectHandler: (callback?: (error?: any) => void) => void
  } {
    const firebaseRef = FirebaseInstance.get().child(path)
    const propName = path.split('/').pop()
    const syncedObjectInstance = {
      onCallBack: null as ((snap: any) => void) | null, // Define the onCallBack property
      currentValue: null,
      on: (callback: (val: any) => void, noNullValues?: boolean) => {
        syncedObjectInstance.onCallBack = firebaseRef.on(
          'value',
          (snap: any) => {
            const val = snap.val()
            syncedObjectInstance.currentValue = val
            if (noNullValues) {
              if (typeof val !== 'undefined' && val !== null) {
                callback(val)
              }
            } else {
              callback(val)
            }
          },
          (error: any) => {
            logger.error('Error in synced firebase object handler', error)
          }
        )
      },
      get: () => {
        return syncedObjectInstance.currentValue
      },
      off: () => {
        firebaseRef.off('value', syncedObjectInstance.onCallBack)
      },
      once: (callback: (val: any) => void, noNullValues?: boolean) => {
        firebaseRef.once(
          'value',
          (snap: any) => {
            const val = snap.val()
            if (noNullValues) {
              if (typeof val !== 'undefined' && val !== null) {
                callback(val)
              }
            } else {
              callback(val)
            }
          },
          (error: any) => {
            logger.error('Error in getting createSyncedObject "once"', error)
          }
        )
      },
      set: (val: any, callback?: (error?: any) => void) => {
        firebaseRef
          .set(val)
          .then(() => {
            if (callback) {
              callback()
            }
          })
          .catch((error: any) => {
            logger.error(`Error setting firebase data at ${path}`, error)
            if (callback) callback(error)
          })
      },
      remove: (callback?: (error?: any) => void) => {
        firebaseRef
          .remove()
          .then(() => {
            if (callback) {
              callback()
            }
          })
          .catch((error: any) => {
            logger.error(`Error removing firebase synced object listener at ${path}`, error)
            if (callback) callback(error)
          })
      },
      increment: (byThisMany: number = 1, childName: string = '') => {
        const ref = childName ? firebaseRef.child(childName) : firebaseRef
        ref.transaction(
          (value: any) => (value ? value + byThisMany : byThisMany),
          FirebaseUtils.error
        )
      },
      decrement: (byThisMany: number = 1, childName: string = '') => {
        const ref = childName ? firebaseRef.child(childName) : firebaseRef
        ref.transaction((value: any) => (value ? value - byThisMany : 0), FirebaseUtils.error)
      },
      push: (val: any, callback?: () => void) => {
        firebaseRef
          .push(val)
          .then(() => {
            if (callback) {
              callback()
            }
          })
          .catch((error: any) => {
            logger.error(`Error setting ${path}`, error)
          })
      },
      setChild: (childName: string, val: any, callback?: () => void) => {
        firebaseRef
          .child(childName)
          .set(val)
          .then(() => {
            if (callback) {
              callback()
            }
          })
          .catch((error: any) => {
            logger.error(`Error setting child on ${path}`, error)
          })
      },
      removeChild: (childName: string, callback?: () => void) => {
        firebaseRef
          .child(childName)
          .remove()
          .then(() => {
            if (callback) {
              callback()
            }
          })
          .catch((error: any) => {
            logger.error(`Error removing firebase child at ${path}`, error)
          })
      },
      update: (val: any, callback?: () => void) => {
        firebaseRef
          .update(val)
          .then(() => {
            if (callback) {
              callback()
            }
          })
          .catch((error: any) => {
            logger.error(`Error updating firebase object at ${path}`, error)
          })
      },
      updateChild: (childName: string, val: any, callback?: () => void) => {
        firebaseRef
          .child(childName)
          .update(val)
          .then(() => {
            if (callback) {
              callback()
            }
          })
          .catch((error: any) => {
            logger.error(`Error updating firebase object child at ${path}`, error)
          })
      },
      setOnDisconnectHandler: (
        action: 'update' | 'remove',
        updates: any = null,
        callback?: (error?: any) => void
      ) => {
        const onDisconnectRef = firebaseRef.onDisconnect()

        const onDisconnectAction =
          action === 'remove' ? onDisconnectRef.remove() : onDisconnectRef.update(updates)

        onDisconnectAction
          .then(() => {
            if (callback) {
              callback()
            }
          })
          .catch((error: any) => {
            logger.error(`Error setting OnDisconnect handler for ${path}`, error)
            if (callback) {
              callback(error)
            }
          })
      },
      cancelOnDisconnectHandler: (callback?: (error?: any) => void) => {
        firebaseRef
          .onDisconnect()
          .cancel()
          .then(() => {
            if (callback) {
              callback()
            }
          })
          .catch((error: any) => {
            logger.error(`Error cancelling OnDisconnect handler for ${path}`, error)
            if (callback) {
              callback(error)
            }
          })
      }
    }

    return syncedObjectInstance
  }

  static serverTimestamp(): any {
    return FirebaseInstance.serverTimestamp()
  }
}

export default FirebaseUtils
